import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AdminLayout } from '../features/admin/AdminLayout';
import { AuditPage } from '../features/admin/AuditPage';
import { LogPage } from '../features/admin/LogPage';
import { SalonsAdminPage } from '../features/admin/SalonsAdminPage';
import { UsersPage } from '../features/admin/UsersPage';
import { ChangePasswordPage } from '../features/auth/ChangePasswordPage';
import { RequireAdmin, RequireAuth } from '../features/auth/guards';
import { LoginPage } from '../features/auth/LoginPage';
import { AnalyticsDetailPage } from '../features/analytics/AnalyticsDetailPage';
import { CompetitionPage } from '../features/competitions/CompetitionPage';
import { CompetitionsPage } from '../features/competitions/CompetitionsPage';
import { ExportPage } from '../features/exports/ExportPage';
import { ImportPage } from '../features/import/ImportPage';
import { ImagesPage } from '../features/images/ImagesPage';
import { SalonSearchPage } from '../features/salons/SalonSearchPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { ComponentsPage } from '../features/system/ComponentsPage';
import { HomePage } from '../features/system/HomePage';
import { NotFoundPage } from '../features/system/NotFoundPage';
import { Layout } from './Layout';

/**
 * Routen der Anwendung. `/login` ist öffentlich; alles andere liegt hinter `RequireAuth`, der
 * Bereich `/admin` zusätzlich hinter `RequireAdmin`. Fachseiten folgen in den nächsten Meilensteinen.
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    children: [
      { path: 'login', element: <LoginPage /> },
      {
        element: <RequireAuth />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'passwort-aendern', element: <ChangePasswordPage /> },
          { path: 'competitions', element: <CompetitionsPage /> },
          { path: 'competitions/new', element: <SalonSearchPage /> },
          { path: 'competitions/:id', element: <CompetitionPage /> },
          { path: 'images', element: <ImagesPage /> },
          { path: 'export', element: <ExportPage /> },
          { path: 'import', element: <ImportPage /> },
          { path: 'analytics/:topic', element: <AnalyticsDetailPage /> },
          { path: 'einstellungen', element: <SettingsPage /> },
          { path: 'komponenten', element: <ComponentsPage /> },
          {
            element: <RequireAdmin />,
            children: [
              {
                path: 'admin',
                element: <AdminLayout />,
                children: [
                  { index: true, element: <Navigate to="/admin/benutzer" replace /> },
                  { path: 'benutzer', element: <UsersPage /> },
                  { path: 'wettbewerbsliste', element: <SalonsAdminPage /> },
                  { path: 'protokoll', element: <LogPage /> },
                  { path: 'audit', element: <AuditPage /> },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

/**
 * Erstellt den Browser-Router. Der Basispfad stammt vom Build (`VITE_BASE`, z. B. `/neu/`),
 * damit die App unter einem Unterpfad ausgeliefert werden kann.
 * @returns Der Router für `RouterProvider`.
 */
export function createAppRouter() {
  const basename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';
  return createBrowserRouter(routes, { basename });
}
